import { useState, useMemo } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Alert,
} from "reactstrap";

import { apiSlice } from "app/apiSlice";
import { showToast } from "components/Toasts/helpers/showToast";
import { ApprovalConfirmationGrid } from "./ApprovalConfirmationGrid";
import { type BulkApprovalModalProps, type MilestoneApproval } from "../types";
import { formatApprovalAmount } from "../utils/formatters";

export const BulkApprovalModal = ({
  isOpen,
  onClose,
  milestones,
  onSuccess,
  actionType,
  approvalType = "selected",
}: BulkApprovalModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [updateMilestoneApproval] =
    apiSlice.useUpdateMilestoneApprovalMutation();

  const { totalAmount, validMilestones } = useMemo(() => {
    // Filter milestones based on action type and current approval status
    const filtered = milestones.filter((m: MilestoneApproval) => {
      if (!m._id) return false; // Must have valid ID

      // For "approve" action: only include unapproved milestones
      if (actionType === "approve") {
        return !m.isPaymentApproved;
      }

      // For "unapprove" action: only include approved milestones
      if (actionType === "unapprove") {
        return m.isPaymentApproved;
      }

      return false;
    });

    const total = filtered.reduce((sum, milestone) => {
      const amount = Number(milestone.holdBalance ?? milestone.rate ?? 0);
      return sum + (Number.isFinite(amount) ? amount : 0);
    }, 0);

    return { totalAmount: total, validMilestones: filtered };
  }, [milestones, actionType]);

  const handleConfirmAction = async () => {
    if (validMilestones.length === 0) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const milestoneIds = validMilestones.map((m) => m._id);
      const isPaymentApproved = actionType === "approve";

      await updateMilestoneApproval({
        milestoneIds,
        isPaymentApproved,
      }).unwrap();

      // Success - show success toast and close modal
      const actionText = actionType === "approve" ? "approved" : "unapproved";
      showToast({
        type: "success",
        message: `Successfully ${actionText} ${validMilestones.length} milestone(s) totaling ${formatApprovalAmount(totalAmount)}.`,
      });

      if (onSuccess) {
        onSuccess();
      } else {
        onClose();
      }
    } catch (err: any) {
      setError(
        err?.data?.message ||
          err?.message ||
          `Failed to ${actionType} milestones. Please try again.`,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const isApproveAction = actionType === "approve";
  const actionLabel = isApproveAction ? "Approve" : "Unapprove";
  const buttonColor = isApproveAction ? "success" : "danger";

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>Confirm Bulk {actionLabel}</ModalHeader>
      <ModalBody>
        {error && (
          <Alert color="danger" className="mb-3">
            <strong>{actionLabel} Failed:</strong> {error}
          </Alert>
        )}

        <div className="mb-4">
          <h6>{actionLabel} Summary</h6>
          <p className="mb-2">
            Do you want to {actionType}{" "}
            {approvalType === "all"
              ? actionType === "approve"
                ? "all unapproved milestones"
                : "all approved milestones"
              : actionType === "approve"
                ? "all selected unapproved milestones"
                : "all selected approved milestones"}
            ?
          </p>
          <ul className="list-unstyled mb-0">
            <li>
              <strong>Number of milestones:</strong> {validMilestones.length}
            </li>
            <li>
              <strong>Total amount to be {actionType}d:</strong>{" "}
              {formatApprovalAmount(totalAmount)}
            </li>
          </ul>
        </div>

        {validMilestones.length > 0 && (
          <div>
            <h6>
              {approvalType === "all"
                ? actionType === "approve"
                  ? "Unapproved milestones"
                  : "Approved milestones"
                : actionType === "approve"
                  ? "Selected unapproved milestones"
                  : "Selected approved milestones"}{" "}
              to be {actionType}d:
            </h6>
            <ApprovalConfirmationGrid
              milestones={validMilestones}
              height={300}
            />
          </div>
        )}

        {validMilestones.length === 0 && (
          <Alert color="warning">
            No {actionType === "approve" ? "unapproved" : "approved"} milestones
            found to {actionType}.
          </Alert>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          color={buttonColor}
          onClick={handleConfirmAction}
          disabled={
            isSubmitting || validMilestones.length === 0 || totalAmount <= 0
          }
        >
          {isSubmitting
            ? "Processing..."
            : `${actionLabel} ${formatApprovalAmount(totalAmount)}`}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
